import { useCallback, useEffect, useState } from 'react';

export type UpdateSinglePropertyFnType<T> = (
  key: keyof T,
  value: T[keyof T]
) => void;

export interface UseObjectStateReturnType<T> {
  state: T;
  updateProperty: UpdateSinglePropertyFnType<T>;
  bulkUpdate: (newState: Partial<T>) => void;
}

export const useObjectState = <T>({
  initialState,
  onStateUpdated,
}: {
  initialState: T;
  onStateUpdated?: (state: T) => void;
}): UseObjectStateReturnType<T> => {
  const [state, setState] = useState<T>(initialState);

  const updateProperty = useCallback((key: keyof T, value: T[keyof T]) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  }, []);

  const bulkUpdate = useCallback((newState: Partial<T>) => {
    setState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  }, []);

  useEffect(() => {
    if (!onStateUpdated) return;
    onStateUpdated(state);
  }, [state, onStateUpdated]);

  return {
    state,
    updateProperty,
    bulkUpdate,
  };
};
